import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { useSite } from "../lib/site";
import Reveal from "../components/Reveal";
import Portrait from "../components/Portrait";
import MediaImg from "../components/MediaImg";

export default function About() {
  const { settings } = useSite();
  const [page, setPage] = useState(null);
  useSeo({ title: "Tentang Saya — Heri Sensei", description: "Perjalanan, nilai, dan bidang yang ditekuni Heri Sensei.", path: "/tentang" });

  useEffect(() => {
    api.get("/content/about").then((r) => setPage(r.data)).catch(() => setPage({}));
  }, []);

  const c = page || {};
  const values = c.values || [];
  const timeline = c.timeline || [];

  return (
    <div data-testid="about-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-14 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <Portrait photoId={c.portrait_id || settings.about_photo_id} alt={c.portrait_alt || settings.about_photo_alt || "Potret Heri Sensei"} className="max-w-sm" ratio="4/5" />
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-enji">{c.eyebrow || "Tentang Saya"}</p>
          <h1 data-testid="about-title" className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight mt-4 text-ink">
            {c.title || settings.full_name || "Heri Ilham, S.S., M.M."}
          </h1>
          <p className="mt-4 text-faint text-sm">{c.subtitle || `${settings.brand_name || "Heri Sensei"} — ${settings.tagline || "pendidik dan penulis"}`}</p>
          <p className="mt-8 text-lg leading-relaxed text-ink" data-testid="about-lead">
            {c.lead || "Halo, saya Heri. Selamat datang di ruang belajar dan bertumbuh saya."}
          </p>
          <div className="reader mt-6 max-w-none" dangerouslySetInnerHTML={{ __html: c.biography || "" }} />
        </div>
      </div>

      {c.journey && (
        <Reveal className="mt-20">
          <h2 className="font-serif text-3xl tracking-tight text-ink">{c.journey_heading || "Kisah Perjalanan"}</h2>
          <div className="reader mt-6 max-w-3xl" dangerouslySetInnerHTML={{ __html: c.journey }} />
        </Reveal>
      )}

      {values.length > 0 && (
        <Reveal className="mt-20">
          <h2 className="font-serif text-3xl tracking-tight text-ink">{c.values_heading || "Nilai yang Saya Pegang"}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {values.map((v, i) => (
              <div key={i} data-testid={`about-value-${i}`} className="bg-surface border border-line rounded-lg p-6">
                <span className="w-2.5 h-2.5 rounded-full bg-enji block" aria-hidden="true" />
                <h3 className="font-serif text-xl mt-4 text-ink">{v.title}</h3>
                <p className="text-sub text-[15px] leading-relaxed mt-2">{v.text}</p>
              </div>
            ))}
          </div>
        </Reveal>
      )}

      {(c.fields || []).length > 0 && (
        <Reveal className="mt-20">
          <h2 className="font-serif text-3xl tracking-tight text-ink">{c.fields_heading || "Bidang yang Saya Tekuni"}</h2>
          <div className="flex flex-wrap gap-3 mt-8" data-testid="about-fields">
            {c.fields.map((f, i) => (
              <span key={i} className="px-5 py-2.5 rounded-full border border-line bg-surface text-[15px] text-ink">
                {f}
              </span>
            ))}
          </div>
        </Reveal>
      )}

      {timeline.length > 0 && (
        <Reveal className="mt-20">
          <h2 className="font-serif text-3xl tracking-tight text-ink">{c.timeline_heading || "Garis Waktu"}</h2>
          <ol className="mt-8 border-l border-line space-y-8 pl-8">
            {timeline.map((t, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[37px] top-1.5 w-3 h-3 rounded-full bg-enji border-2 border-ivory" aria-hidden="true" />
                <p className="font-serif text-xl text-ink">{t.year}</p>
                <p className="text-sub mt-1">{t.event}</p>
              </li>
            ))}
          </ol>
        </Reveal>
      )}

      {(c.activities_media || []).length > 0 && (
        <Reveal className="mt-20">
          <h2 className="font-serif text-3xl tracking-tight text-ink">{c.activities_heading || "Foto Kegiatan"}</h2>
          {c.activities_note && <p className="text-sub mt-4">{c.activities_note}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
            {c.activities_media.map((m, i) => (
              <MediaImg key={i} id={m} alt={(c.activities_alt || [])[i] || `Foto kegiatan ${i + 1}`} ratio="3/2" className="rounded-lg border border-line" />
            ))}
          </div>
        </Reveal>
      )}

      <Reveal className="mt-20 flex flex-wrap gap-4">
        <Link to="/tulisan" data-testid="about-to-articles" className="btn-primary">{c.cta_primary_label || "Jelajahi Tulisan"}</Link>
        <Link to="/konsultasi" data-testid="about-to-consultation" className="btn-ghost">{c.cta_secondary_label || "Ajukan Konsultasi"}</Link>
      </Reveal>
    </div>
  );
}
